import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Heart, Cat, Flower2, Gift } from "lucide-react";

interface ImpossibleNoButtonProps {
  onAttempt: () => void;
  attempts: number;
}

type DodgeBehavior = 
  | "teleport" 
  | "shrink" 
  | "clone" 
  | "transform" 
  | "melt" 
  | "spin" 
  | "explode" 
  | "ghost" 
  | "bounce" 
  | "loveBomb"
  | "splitAndRun"
  | "pretendClick";

interface CloneButton {
  id: number;
  x: number;
  y: number;
  delay: number;
}

interface FloatingEmoji {
  id: number;
  emoji: string;
  x: number;
  y: number;
}

const ImpossibleNoButton = ({ onAttempt, attempts }: ImpossibleNoButtonProps) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const [isGlitching, setIsGlitching] = useState(false);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [currentTransform, setCurrentTransform] = useState<string | null>(null);
  const [clones, setClones] = useState<CloneButton[]>([]);
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const [loveBombMessages, setLoveBombMessages] = useState<FloatingEmoji[]>([]);
  const [isBouncing, setIsBouncing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bounceIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const transforms = ["🐱", "🌸", "🎁", "💖", "🐶", "🍫", "🌹"];

  const loveMessages = [
    "I Love You! 💕",
    "Please? 🥺",
    "Say YES! 💖",
    "Pretty Please! 🌹",
    "Come on! 😘",
    "You know you want to! 💗",
    "Don't be shy! 💝",
    "YES is the answer! ❤️",
  ];

  const getRandomPosition = useCallback(() => {
    const maxX = Math.min(window.innerWidth * 0.4, 300);
    const maxY = Math.min(window.innerHeight * 0.3, 200);
    return {
      x: (Math.random() - 0.5) * maxX * 2,
      y: (Math.random() - 0.5) * maxY * 2,
    };
  }, []);

  const teleport = useCallback(() => {
    setIsGlitching(true);
    setIsVisible(false);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setIsVisible(true);
      setTimeout(() => setIsGlitching(false), 200);
    }, 150);
  }, [getRandomPosition]);

  const shrink = useCallback(() => {
    setScale(0);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setScale(1);
    }, 400);
  }, [getRandomPosition]);

  const clone = useCallback(() => {
    const newClones: CloneButton[] = Array.from({ length: 8 }, (_, i) => ({
      id: Date.now() + i,
      x: (Math.random() - 0.5) * 400,
      y: (Math.random() - 0.5) * 300,
      delay: i * 0.05,
    }));
    setClones(newClones);
    setTimeout(() => setClones([]), 2000);
    setPosition(getRandomPosition());
  }, [getRandomPosition]);

  const transform = useCallback(() => {
    const emoji = transforms[Math.floor(Math.random() * transforms.length)];
    setCurrentTransform(emoji);
    setTimeout(() => {
      setCurrentTransform(null);
      setPosition(getRandomPosition());
    }, 800);
  }, [getRandomPosition]);

  const melt = useCallback(() => {
    setScale(0.1);
    setOpacity(0);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setScale(1);
      setOpacity(1);
    }, 600);
  }, [getRandomPosition]);

  const spin = useCallback(() => {
    setRotation(720);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setRotation(0);
    }, 500);
  }, [getRandomPosition]);

  const explode = useCallback(() => {
    const emojis = ["💖", "💕", "💗", "💓", "❤️", "✨", "💫", "🌟"];
    const newEmojis: FloatingEmoji[] = Array.from({ length: 12 }, (_, i) => ({
      id: Date.now() + i,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      x: (Math.random() - 0.5) * 200,
      y: (Math.random() - 0.5) * 200,
    }));
    setFloatingEmojis(newEmojis);
    setIsVisible(false);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setIsVisible(true);
      setFloatingEmojis([]);
    }, 800);
  }, [getRandomPosition]);

  const ghost = useCallback(() => {
    setOpacity(0.1);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setOpacity(1);
    }, 600);
  }, [getRandomPosition]);

  const bounce = useCallback(() => {
    setIsBouncing(true);
    let bounceCount = 0;
    bounceIntervalRef.current = setInterval(() => {
      setPosition(getRandomPosition());
      bounceCount++;
      if (bounceCount >= 5) {
        if (bounceIntervalRef.current) clearInterval(bounceIntervalRef.current);
        setIsBouncing(false);
      }
    }, 200);
  }, [getRandomPosition]);

  const loveBomb = useCallback(() => {
    const messages: FloatingEmoji[] = Array.from({ length: 8 }, (_, i) => ({
      id: Date.now() + i,
      emoji: loveMessages[Math.floor(Math.random() * loveMessages.length)],
      x: (Math.random() - 0.5) * 400,
      y: (Math.random() - 0.5) * 300,
    }));
    setLoveBombMessages(messages);
    setIsVisible(false);
    setTimeout(() => {
      setPosition(getRandomPosition());
      setIsVisible(true);
      setLoveBombMessages([]);
    }, 1500);
  }, [getRandomPosition]);

  const splitAndRun = useCallback(() => {
    const splitClones: CloneButton[] = [
      { id: Date.now(), x: -150, y: 0, delay: 0 },
      { id: Date.now() + 1, x: 150, y: 0, delay: 0 },
    ];
    setClones(splitClones);
    setIsVisible(false);
    setTimeout(() => {
      setClones([]);
      setPosition(getRandomPosition());
      setIsVisible(true);
    }, 800);
  }, [getRandomPosition]);

  const pretendClick = useCallback(() => {
    setScale(0.9);
    setTimeout(() => {
      setScale(1.1);
      setPosition(getRandomPosition());
      setTimeout(() => setScale(1), 150);
    }, 100);
  }, [getRandomPosition]);

  const allBehaviors = [
    teleport, shrink, clone, transform, melt, 
    spin, explode, ghost, bounce, loveBomb, 
    splitAndRun, pretendClick
  ];

  const executeDodge = useCallback(() => {
    const randomBehavior = allBehaviors[Math.floor(Math.random() * allBehaviors.length)];
    randomBehavior();
    onAttempt();
  }, [allBehaviors, onAttempt]);

  const handleMouseEnter = () => {
    if (attempts < 5) {
      executeDodge();
    }
  };

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (attempts < 5) {
      executeDodge();
    }
  };

  // Cleanup bouncing on unmount
  useEffect(() => {
    return () => {
      if (bounceIntervalRef.current) {
        clearInterval(bounceIntervalRef.current);
      }
    };
  }, []);

  // Auto-transform to YES after 5 attempts
  if (attempts >= 5) {
    return (
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", damping: 10 }}
        className="relative"
      >
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0],
          }}
          transition={{ duration: 0.5, repeat: 3 }}
        >
          <div className="text-6xl sm:text-8xl">💖</div>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-pink-300 text-center mt-2 text-sm"
        >
          Fine, it's a YES! 🎉
        </motion.p>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      {/* Clone buttons */}
      <AnimatePresence>
        {clones.map((cloneItem) => (
          <motion.div
            key={cloneItem.id}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 0.6, 
              scale: 0.8,
              x: cloneItem.x,
              y: cloneItem.y,
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ delay: cloneItem.delay }}
            className="absolute pointer-events-none"
          >
            <Button
              size="lg"
              className="text-xl px-12 py-8 bg-gradient-to-r from-rose-500 to-red-500 opacity-50"
            >
              NO 😢
            </Button>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Exploding emojis */}
      <AnimatePresence>
        {floatingEmojis.map((emoji) => (
          <motion.span
            key={emoji.id}
            initial={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            animate={{ 
              opacity: 0, 
              scale: 2,
              x: emoji.x,
              y: emoji.y,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute text-2xl pointer-events-none"
            style={{ left: "50%", top: "50%" }}
          >
            {emoji.emoji}
          </motion.span>
        ))}
      </AnimatePresence>

      {/* Love bomb messages */}
      <AnimatePresence>
        {loveBombMessages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              x: msg.x,
              y: msg.y,
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ type: "spring", damping: 15 }}
            className="absolute text-pink-300 font-bold text-lg whitespace-nowrap pointer-events-none"
            style={{ left: "50%", top: "50%" }}
          >
            {msg.emoji}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Main Button */}
      <motion.div
        animate={{
          x: position.x,
          y: position.y,
          scale: scale,
          rotate: rotation,
          opacity: opacity,
        }}
        transition={{
          type: isBouncing ? "tween" : "spring",
          damping: isBouncing ? undefined : 15,
          duration: isBouncing ? 0.15 : undefined,
        }}
        style={{ display: isVisible ? "block" : "none" }}
      >
        {currentTransform ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1.5 }}
            className="text-4xl"
          >
            {currentTransform}
          </motion.div>
        ) : (
          <Button
            ref={buttonRef}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            onTouchStart={handleClick}
            size="lg"
            className={`text-xl px-12 py-8 bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600 shadow-lg transition-all ${
              isGlitching ? "animate-pulse" : ""
            }`}
          >
            {isGlitching ? (
              <motion.span
                animate={{ x: [-2, 2, -2, 0] }}
                transition={{ duration: 0.1, repeat: 3 }}
              >
                NO 😢
              </motion.span>
            ) : (
              "NO 😢"
            )}
          </Button>
        )}
      </motion.div>

      {/* Attempt counter */}
      {attempts > 0 && attempts < 5 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-pink-400/60 text-xs whitespace-nowrap"
        >
          Nice try! ({5 - attempts} more to go 😉)
        </motion.p>
      )}
    </div>
  );
};

export default ImpossibleNoButton;
